import { StableKey } from 'app/models/common-types'
import { TradedFacilityParams } from 'app/models/traded-facilities-params'
import { getFundedAndUnfundedTradeAmount } from '../../../utils/master-trade-util'
import { FMCalculatedFee, FMFacility, FMFacilityAmounts, FMSubTradeFee } from 'app/models/fm-metadata-params'

// Define custom display order
const PRIORITY_ORDER = [
  'Funded Amount',
  'Unfunded Amount',
  'Economic Benefit',
  'Delayed Compensation',
  'Cost of Carry',
  'Assignment Fee',
]

// Helper: get the last amount from a list
export const getLastAmount = (
  amounts: Array<{ amount: string }> = [],
): number => Number(amounts.at(-1)?.amount ?? 0)

// Helper: calculate percentage-based value
export const calculatePctValue = (amount: number, price?: string): number =>
  price != null ? (amount * Number(price)) / 100 : 0

// Sort items by defined priority, then alphabetically
export const applyPrioritySort = (
  items: Array<{ label: string; value: number | string; currency?: string }>,
): Array<{ label: string; value: number | string; currency?: string }> => {
  return items.sort((a, b) => {
    const rank = (label: string) => {
      const idx = PRIORITY_ORDER.findIndex(p => label.startsWith(p))
      return idx >= 0 ? idx : PRIORITY_ORDER.length
    }
    const ra = rank(a.label)
    const rb = rank(b.label)
    if (ra !== rb) return ra - rb
    return a.label.localeCompare(b.label)
  })
}

export function getSubTradeLevelFees(
  subTradeFees: FMSubTradeFee[],
  currency: string,
): Array<{ label: string; value: number; currency?: string }> {
  return Object.entries(
    (subTradeFees).reduce<Record<string, number>>((acc, fee) => {
      const key = fee.feeType?.trim() ?? ''
      const amt = Number(fee.feeAmount) || 0
      const signed = fee.paidTo === 'PaidToBuyer' ? -amt : amt
      acc[key] = (acc[key] || 0) + signed
      return acc
    }, {}),
  ).map(([feeType, total]) => ({
    label: feeType,
    value: total,
    // Sub-trade level fees typically inherit from the first facility's currency or default to USD
    currency: currency
      
  }))
}

export function generateSubTradeRowsHtml(
  subTradeFees: FMSubTradeFee[],
  facilityAmounts: FMFacilityAmounts[],
  tradedFacilityWithCalculatedFee: any,
  targetSettlementDate: string | null,
) {
  // 1) Funded Amount per facility
  const fundedCalcs = tradedFacilityWithCalculatedFee.flatMap((tf:any) => {
    const type = tf.facilityDetails?.facilityType ?? 'Unknown'
    // PIK travels free: pro-rata on full amount first, then subtract PIK
    // Correct formula: price × (fundedPrincipal - PIK)  NOT: price × (amount - PIK) × fundedProportion
    const fullAmount = getLastAmount(tf.amounts)
    const pikAmount = tf.pikAmount ?? 0
    const { fundedAmount: rawFundedAmount, unfundedAmount } = getFundedAndUnfundedTradeAmount(
      fullAmount,
      Number(tf.price),
      targetSettlementDate
        ? new Date(targetSettlementDate)
        : new Date(),
      facilityAmounts,
      tf.facilityId.id,
    )
    // Subtract PIK after pro-rata: fundedAmount = rawFundedAmount - PIK × price/100
    const fundedAmount = rawFundedAmount - (pikAmount * Number(tf.price) / 100)
    const result = []
    if (fundedAmount !== 0)
      result.push({
        label: `Funded Amount - ${type}`,
        value: fundedAmount,
        currency: tf.facilityDetails?.baseCurrency || 'USD',
      })
    if (unfundedAmount !== 0)
      result.push({
        label: `Unfunded Amount - ${type}`,
        value: unfundedAmount,
        currency: tf.facilityDetails?.baseCurrency || 'USD',
      })

    return result
  })

  // 2) Economic Benefit (main row — calculated only).
  // The FM document shows the *calculated* default in the main row; any override
  // is rendered as a separate "Override X" row carrying the adjustment delta
  // (see generateHTML.ts). Falling back to `overrideAmount` covers historical
  // locked FM metadata where the calculated value was stored under that field.
  const calculatedFeeAmount = (fee: FMCalculatedFee) =>
    Number(fee.calculatedAmount ?? fee.overrideAmount ?? 0)

  const econBenefitCalcs = tradedFacilityWithCalculatedFee
    .flatMap(
      (f: TradedFacilityParams) =>{
        return f.fees.map(fee => ({ ...fee, facilityId: f.facilityId, facilityDetails: f.facilityDetails })) || []
      },
    )
    .filter(
      (fee: FMCalculatedFee & { facilityId: StableKey, facilityDetails: FMFacility }) =>
        fee.feeType === 'Economic Benefit' && calculatedFeeAmount(fee) > 0,
    )
    .map((fee: FMCalculatedFee & { facilityId: StableKey, facilityDetails: FMFacility }) => {
      const signed = fee.paidTo === 'PaidToBuyer' ? -1 : 1
      const amount = calculatedFeeAmount(fee)
      return {
        label: `Economic Benefit${fee?.facilityDetails?.facilityType ? ' - ' + fee.facilityDetails.facilityType : ''}`,
        value: amount > 0 ? signed * amount : 'N/A',
        currency: fee?.facilityDetails?.baseCurrency || 'USD',
      }
    })

  // 3) All other facility fees (excluding Economic Benefit)
  const otherFacilityFees = tradedFacilityWithCalculatedFee
    .flatMap((f: { fees: FMCalculatedFee[] }) => f.fees || [])
    .filter(
      (fee: { feeType: string }) => fee.feeType?.trim() !== 'Economic Benefit',
    )

  const groupedFacilityFees = Object.entries(
    otherFacilityFees.reduce(
      (acc: Record<string, number>, fee: FMCalculatedFee) => {
        const key = fee.feeType?.trim() ?? ''
        const amt = calculatedFeeAmount(fee)
        const signed = fee.paidTo === 'PaidToBuyer' ? -amt : amt
        acc[key] = (acc[key] || 0) + signed
        return acc
      },
      {} as Record<string, number>,
    ),
  ).map(
    ([feeType, net]): { label: string; value: number; currency: string } => ({
      label: feeType,
      value: Number(net),
      currency:
        tradedFacilityWithCalculatedFee[0]?.facilityDetails?.baseCurrency ||
        '',
    }),
  )

  // 4) Sub-trade level fees (reuseable)
  const groupedSubTradeFees = getSubTradeLevelFees(subTradeFees as any, tradedFacilityWithCalculatedFee[0]?.facilityDetails?.baseCurrency)

  // Combine and sort all calculations
  const sortedCalculations = applyPrioritySort([
    ...econBenefitCalcs,
    ...groupedFacilityFees,
    ...groupedSubTradeFees,
  ])
  const calculations = [...fundedCalcs, ...sortedCalculations]

  // Render into HTML rows
  return calculations
}
